// ----------------------------------------------------------------------

import { DueMarker } from "src/services/tasks/tasks.types";

import { TenantType } from "src/types/enums";

const ROOTS = {
  AUTH: '/auth',
  SETUP: '/onboarding',
  DASHBOARD: '/dashboard',
};

// ----------------------------------------------------------------------

export const paths = {
  // AUTH
  auth: {
    login: `${ROOTS.AUTH}/login`,
    register: {
      root: `${ROOTS.AUTH}/register`,
      confirm: `${ROOTS.AUTH}/register/confirm`,
      account: `${ROOTS.AUTH}/register/account`,
      verify: `${ROOTS.AUTH}/register/account/verify`,
      organisation: `${ROOTS.AUTH}/register/organisation`
    },
    reset: `${ROOTS.AUTH}/password-reset`,
    accept_invite: `${ROOTS.AUTH}/accept-invite`,
    offline_verification: `${ROOTS.AUTH}/complete-registration`,
    callbacks: {
      job_adder: `${ROOTS.AUTH}/jobadder/callback`,
    },
  },
  setup: {
    candidate: {
      profile: `${ROOTS.SETUP}/candidate/profile`,
      invites: `${ROOTS.SETUP}/candidate/invites`,
    },
    organization: {
      invites: `${ROOTS.SETUP}/organisation/invites`,
      billing: {
        choosePlan: `${ROOTS.SETUP}/organisation/billing/choose-plan`,
      },
    }

  },
  // DASHBOARD
  dashboard: {
    root: `${ROOTS.DASHBOARD}/activity`,
    account: {
      root: `${ROOTS.DASHBOARD}/account`,
      security: `${ROOTS.DASHBOARD}/account/security`,
      notifications: `${ROOTS.DASHBOARD}/account/notifications`,
      settings: `${ROOTS.DASHBOARD}/account/settings`,
      email: `${ROOTS.DASHBOARD}/account/email`,
      availability: `${ROOTS.DASHBOARD}/account/availability-management`,
      scheduling: `${ROOTS.DASHBOARD}/account/scheduling`,
    },
    profile: {
      root: `${ROOTS.DASHBOARD}/profile`,
      documents: `${ROOTS.DASHBOARD}/documents`,
      referees: {
        root: `${ROOTS.DASHBOARD}/referees`,
        detail: (id: string) => `${ROOTS.DASHBOARD}/referees/${id}`,
      },
      edit: `${ROOTS.DASHBOARD}/profile`,
    },
    candidates: {
      root: `${ROOTS.DASHBOARD}/candidates`,
      detail: (id: string, tab: string = 'detail') => `${ROOTS.DASHBOARD}/candidates/${id}?tab=${tab}`,
      search: `${ROOTS.DASHBOARD}/candidates/search`,
    },
    jobs: {
      root: `${ROOTS.DASHBOARD}/jobs`,
      create: `${ROOTS.DASHBOARD}/jobs/create`,
      create_link: (linkRequestId: string) =>
        `${ROOTS.DASHBOARD}/jobs/create?linkRequestId=${linkRequestId}`,
      edit: (id: string, viewMode: 'view' | 'edit' = 'view') =>
        `${ROOTS.DASHBOARD}/jobs/${id}/editor`,
      detail: (id: string, tab: string | undefined = undefined) => `${ROOTS.DASHBOARD}/jobs/${id}${tab ? `?tab=${tab}` : ''}`,
      application: (jobId: string | undefined, applicationId: string, tab: string | undefined = undefined) =>
        `${ROOTS.DASHBOARD}/jobs/${jobId}/applications/${applicationId}${tab ? `?tab=${tab}` : ''}`,
    },
    contacts: {
      root: `${ROOTS.DASHBOARD}/contacts`,
      detail: (id: string) => `${ROOTS.DASHBOARD}/contacts/${id}`,
    },
    companies: {
      root: `${ROOTS.DASHBOARD}/companies`,
      clients: `${ROOTS.DASHBOARD}/companies/clients`,
      recruiters: `${ROOTS.DASHBOARD}/companies/recruiters`,
      all: `${ROOTS.DASHBOARD}/companies/all`,
      detail: (id: string, tenantType: TenantType, companyType: TenantType) => {
        if ([TenantType.Client, TenantType.Recruiter].includes(tenantType)) {
          return `${paths.dashboard.companies.all}/${id}`;
        }

        return `${paths.dashboard.companies.root}/${companyType}s/${id}`;

      }
    },
    lists: {
      root: `${ROOTS.DASHBOARD}/lists`,
      detail: (id: string) => `${ROOTS.DASHBOARD}/lists/${id}`,
    },
    notetaker: {
      root: `${ROOTS.DASHBOARD}/notetaker`,
      detail: (id: string) => `${ROOTS.DASHBOARD}/notetaker/${id}`,
    },
    calendar: {
      root: `${ROOTS.DASHBOARD}/calendar?view=grid`,
    },
    reports: {
      root: `${ROOTS.DASHBOARD}/admin/reports`,
      detail: (reportType: string) => `${ROOTS.DASHBOARD}/admin/reports/${reportType}`,
    },
    admin: {
      organization: {
        root: `${ROOTS.DASHBOARD}/admin/organisation`,
        company_profile: `${ROOTS.DASHBOARD}/admin/organisation/company-profile`,
        users: `${ROOTS.DASHBOARD}/admin/organisation/users`,
        department: `${ROOTS.DASHBOARD}/admin/organisation/departments`,
        settings: (tab?: string) => `${ROOTS.DASHBOARD}/admin/organisation/settings${tab ? `?tab=${tab}` : ''}`,
        domains: `${ROOTS.DASHBOARD}/admin/organisation/domains`,
      },
      connection_management: {
        root: `${ROOTS.DASHBOARD}/admin/connections`,
        detail: (id: string) => `${ROOTS.DASHBOARD}/admin/connections/${id}`,
      },
      billing: {
        root: `${ROOTS.DASHBOARD}/admin/billing`,
      },
      documents: {
        root: `${ROOTS.DASHBOARD}/admin/documents`,
      },
      inbound_email_activity: {
        root: `${ROOTS.DASHBOARD}/admin/inbound-email-activity`,
      },
      integrations: {
        root: `${ROOTS.DASHBOARD}/admin/integrations`,
        detail: {
          configuration: (id: string) =>
            `${ROOTS.DASHBOARD}/admin/integrations/${id}/configuration`,
          activity: (id: string) => `${ROOTS.DASHBOARD}/admin/integrations/${id}/activity`,
          communications: (id: string) => `${ROOTS.DASHBOARD}/admin/integrations/${id}/communications`,
          user_mapping: (id: string) => `${ROOTS.DASHBOARD}/admin/integrations/${id}/user-mapping`,
        },
      }
    },
    restricted: {
      tenants: {
        root: `${ROOTS.DASHBOARD}/restricted/tenants`,
      },
      accounts: { root: `${ROOTS.DASHBOARD}/restricted/accounts` },
      settings: { root: `${ROOTS.DASHBOARD}/restricted/settings` },
      kb: {
        root: `${ROOTS.DASHBOARD}/restricted/kb`,
        new: `${ROOTS.DASHBOARD}/restricted/kb/new`,
        import: `${ROOTS.DASHBOARD}/restricted/kb/import`,
        edit: (id: string) => `${ROOTS.DASHBOARD}/restricted/kb/${id}`,
      },
    },
    tasks: {
      root: (dueMarker: DueMarker | undefined = undefined) => `${ROOTS.DASHBOARD}/tasks${dueMarker ? `?tab=${dueMarker}` : ''}`,
      detail: (taskId: string) => `${ROOTS.DASHBOARD}/tasks/${taskId}`,
    },
    placements: {
      root: `${ROOTS.DASHBOARD}/placements`,
      detail: (id: string) => `${ROOTS.DASHBOARD}/placements/${id}`,
    },
    deal_flow: {
      root: `${ROOTS.DASHBOARD}/deal-flow`,
      pipeline: (pipelineId: string) => `${ROOTS.DASHBOARD}/deal-flow?pipeline=${pipelineId}`,
      deals: {
        detail: (dealId: string) => `${ROOTS.DASHBOARD}/deal-flow/deals/${dealId}`,
      }
    }
  },
  help: {
    root: '/help',
    search: '/help/search',
    // Category landing reuses the search view with a category query param;
    // there's no dedicated /help/c/:slug view in v1.
    category: (slug: string) => `/help/search?category=${encodeURIComponent(slug)}`,
    detail: (slug: string) => `/help/d/${slug}`,
  },
  experience_verification: '/experience-verification',
  job_board: {
    view: (boardId: string) => `/job-boards/${boardId}/jobs`,
    job: (boardId: string, jobId: string) => `/job-boards/${boardId}/jobs/${jobId}`
  },
  event_scheduler: {
    event_view: (eventId: string) => `/event-scheduler/events/${eventId}`,
  }
};
